package org.zq.astrock_ai.ui.research.gushi.data

import org.zq.astrock_ai.network.StockMarketContext
import org.zq.astrock_ai.ui.research.gushi.model.Candle
import org.zq.astrock_ai.ui.research.gushi.model.HoldingPosition
import org.zq.astrock_ai.ui.research.gushi.model.NewsItem
import org.zq.astrock_ai.ui.research.gushi.model.NewsType
import org.zq.astrock_ai.ui.research.gushi.model.StockItem
import org.zq.astrock_ai.util.formatDecimal

/**
 * 把研究台当前可见数据整理成给大模型的完整简报。
 */
object ResearchChatBrief {
    fun build(
        stock: StockItem,
        metrics: StockTrendMetrics,
        holding: HoldingPosition? = null,
        candles: List<Candle> = emptyList(),
        market: StockMarketContext? = null,
        news: List<NewsItem> = emptyList(),
        adaptive: AdaptiveLearningResult? = null,
        costPrice: Double? = null,
        quantity: Int? = null,
        buyLow: Double = 0.0,
        buyHigh: Double = 0.0,
        buyMethod: String = "",
        buyConfidence: Double = 0.0,
        buyReasons: List<String> = emptyList(),
        sellLow: Double = 0.0,
        sellHigh: Double = 0.0,
        sellMethod: String = "",
        sellConfidence: Double = 0.0,
        sellReasons: List<String> = emptyList(),
    ): String = buildString {
        appendLine("===== 当前研究台实时数据（请优先依据这些数据回答）=====")
        appendLine("【行情】")
        appendLine("股票：${stock.name}（${stock.code} / ${stock.marketCode}）")
        appendLine("现价：${stock.price}  涨跌额：${stock.changeAmount}  涨跌幅：${stock.changePercent}")
        appendLine("行业：${stock.industry}  标签：${stock.tags.joinToString("、").ifBlank { "--" }}")
        appendLine("状态备注：${stock.status} / ${stock.note}")

        appendLine()
        appendLine("【技术面结论】")
        appendLine("状态：${metrics.status}  位置：${metrics.position}  风险：${metrics.riskLevel}")
        appendLine("支撑：${metrics.support}  压力：${metrics.resistance}")
        appendLine("上行概率：${metrics.upsideProb}%  走弱概率：${metrics.weakenProb}%")
        appendLine("分析：${metrics.analysis}")
        appendLine("建议：${metrics.adviceTitle}")
        appendLine("仓位：${metrics.positionDesc}")
        appendLine("买入计划：${metrics.buyPlan}")
        appendLine("风险提示：${metrics.riskHint}")

        if (buyLow > 0 && buyHigh > 0) {
            appendLine()
            appendLine("【程序建议买入区间】（多锚点历史支撑推导，研究参考，非必达价）")
            appendLine(
                "区间：${formatDecimal(buyLow)}–${formatDecimal(buyHigh)}" +
                    if (buyMethod.isNotBlank()) "  方法：$buyMethod" else "",
            )
            if (buyConfidence > 0) {
                appendLine("置信：${formatDecimal(buyConfidence * 100, 0)}%")
            }
            buyReasons.take(6).forEach { appendLine("- $it") }
        }

        if (sellLow > 0 && sellHigh > 0) {
            appendLine()
            appendLine("【程序建议卖出区间】（多锚点历史压力推导，研究参考，非必达价）")
            appendLine(
                "区间：${formatDecimal(sellLow)}–${formatDecimal(sellHigh)}" +
                    if (sellMethod.isNotBlank()) "  方法：$sellMethod" else "",
            )
            if (sellConfidence > 0) {
                appendLine("置信：${formatDecimal(sellConfidence * 100, 0)}%")
            }
            sellReasons.take(6).forEach { appendLine("- $it") }
        }

        if (candles.isNotEmpty()) {
            appendLine()
            appendLine("【最近K线】共 ${candles.size} 根，最近 10 根：")
            candles.takeLast(10).forEach { c ->
                appendLine(
                    "${c.date.ifBlank { "--" }} 开${formatDecimal(c.open)} 高${formatDecimal(c.high)} " +
                        "低${formatDecimal(c.low)} 收${formatDecimal(c.close)} 量${formatVolume(c.volume)}",
                )
            }
        }

        if (market != null) {
            appendLine()
            appendLine("【资金/板块/概念】")
            appendLine("市场类型：${market.market.ifBlank { "--" }}  行业：${market.industry.ifBlank { "--" }}")
            appendLine("概念：${market.concepts.take(12).joinToString("、").ifBlank { "--" }}")
            appendLine("总市值：${money(market.totalMarketCap)}  成交额：${money(market.turnoverAmount)}")
            appendLine("换手率：${market.turnoverRate?.let { "${formatDecimal(it)}%" } ?: "--"}")
            appendLine("今日主力净流入：${money(market.todayMainNet)}  近5日主力净流入：${money(market.recentMainNet)}")
            if (market.strongSectors.isNotEmpty()) {
                appendLine(
                    "强势板块：" + market.strongSectors.joinToString("；") {
                        "${it.name}(${if (it.changePercent > 0) "+" else ""}${formatDecimal(it.changePercent)}%)"
                    },
                )
            }
        }

        if (adaptive != null && (adaptive.historicalSamples > 0 || adaptive.marketRegime.isNotBlank())) {
            appendLine()
            appendLine("【自适应训练/大盘影响】")
            appendLine("基准：${adaptive.benchmarkName.ifBlank { "--" }}  市场：${adaptive.marketRegime.ifBlank { "--" }}")
            appendLine("大盘原因：${adaptive.marketCause.ifBlank { "--" }}")
            appendLine("对个股影响：${adaptive.stockImpact.ifBlank { "--" }}")
            appendLine(
                "Beta：${adaptive.beta?.let { formatDecimal(it) } ?: "--"}  " +
                    "相关性：${adaptive.correlation?.let { formatDecimal(it) } ?: "--"}",
            )
            appendLine("权重：${adaptive.weightSummary.ifBlank { "--" }}")
            appendLine("多周期验证：${adaptive.horizonSummary.ifBlank { "--" }}")
            appendLine(
                "样本外验证：${adaptive.validationSamples}次 / " +
                    "${adaptive.validationHitRate?.let { "${formatDecimal(it)}%" } ?: "--"}",
            )
            appendLine(
                "真实复盘：${adaptive.forwardSamples}次 / " +
                    "${adaptive.forwardHitRate?.let { "${formatDecimal(it)}%" } ?: "--"}",
            )
            appendLine("分数调整：${adaptive.scoreAdjustment}")
        }

        val cost = costPrice ?: holding?.costPrice
        val qty = quantity ?: holding?.quantity
        if (cost != null || qty != null || holding != null) {
            appendLine()
            appendLine("【持仓】")
            appendLine("成本：${cost?.let { formatDecimal(it) } ?: "--"}")
            appendLine("数量：${qty ?: "--"}")
            if (cost != null && qty != null) {
                val px = stock.price.replace(",", "").toDoubleOrNull()
                if (px != null && cost > 0) {
                    val pnl = (px - cost) / cost * 100.0
                    val amt = (px - cost) * qty
                    appendLine("浮动盈亏：${formatDecimal(pnl, forceSign = true)}% / 约 ${formatDecimal(amt, 0, forceSign = true)} 元")
                }
            }
        }

        if (news.isNotEmpty()) {
            appendLine()
            appendLine("【最新动态】最近 ${minOf(12, news.size)} 条：")
                news.take(12).forEach { item ->
                val typeLabel = when (item.type) {
                    NewsType.NEWS -> "新闻"
                    NewsType.CAPITAL -> "资金"
                    NewsType.ANNOUNCEMENT -> "公告"
                    NewsType.FINANCIAL -> "财务"
                    NewsType.RESEARCH -> "研报"
                }
                appendLine("- [$typeLabel] ${item.time} ${item.title}")
            }
        }

        appendLine()
        appendLine("===== 数据结束 =====")
        appendLine("请先消化以上数据，再回答用户问题；不要编造未出现的数字。")
    }.trim()

    private fun money(value: Double?): String {
        if (value == null) return "--"
        val absolute = kotlin.math.abs(value)
        val body = when {
            absolute >= 100_000_000 -> "${formatDecimal(absolute / 100_000_000)}亿"
            absolute >= 10_000 -> "${formatDecimal(absolute / 10_000)}万"
            else -> formatDecimal(absolute, 0)
        }
        return if (value < 0) "-$body" else body
    }

    private fun formatVolume(volume: Double): String {
        val absolute = kotlin.math.abs(volume)
        return when {
            absolute >= 100_000_000 -> "${formatDecimal(absolute / 100_000_000)}亿"
            absolute >= 10_000 -> "${formatDecimal(absolute / 10_000)}万"
            else -> formatDecimal(absolute, 0)
        }
    }
}
